<template>
  <div class="login-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="login-shell">
      <!-- Etiqueta superior -->
      <div class="tag-eyebrow fade-in">
        <span class="tag-dot"></span>
        BAMBOO&nbsp;/&nbsp;Management
      </div>

      <div class="login-card fade-in-up">
        <!-- Regla lateral -->
        <div class="ruler" aria-hidden="true">
          <span
            v-for="n in 12"
            :key="n"
            class="tick"
            :class="{ major: n % 4 === 0 }"
          ></span>
        </div>

        <div class="login-card-inner">
          <!-- Header -->
          <div class="login-head">
            <v-img
              :src="logoBambo"
              contain
              width="140"
              class="login-logo"
            ></v-img>

            <h1 class="login-title">Iniciar sesión</h1>

            <p class="login-sub">
              Panel de administración del gimnasio
            </p>
          </div>

          <!-- Formulario -->
          <form class="login-form" @submit.prevent="iniciarSesion">
            <div class="field">
              <label class="field-label" for="usuario">
                Usuario
              </label>

              <input
                id="usuario"
                v-model="usuario"
                type="text"
                class="field-input"
                placeholder="tu.usuario"
                autocomplete="username"
                :disabled="loading || iniciandoSistema"
              />
            </div>

            <div class="field">
              <div class="field-label-row">
                <label class="field-label" for="password">
                  Contraseña
                </label>

                <button
                  type="button"
                  class="field-toggle"
                  :disabled="loading || iniciandoSistema"
                  @click="showPassword = !showPassword"
                >
                  {{ showPassword ? "Ocultar" : "Mostrar" }}
                </button>
              </div>

              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="field-input"
                placeholder="••••••••"
                autocomplete="current-password"
                :disabled="loading || iniciandoSistema"
              />
            </div>

            <button
              type="submit"
              class="submit-btn"
              :disabled="loading || iniciandoSistema"
            >
              <span v-if="!loading">
                Ingresar
              </span>

              <span v-else class="auth-loading">
                Verificando
                <span class="auth-dots">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </span>
            </button>

            <div class="login-links">
              <button
                type="button"
                class="link-btn"
                :disabled="loading || iniciandoSistema"
                @click="abrirSoporte"
              >
                Soporte
              </button>

              <span class="link-sep">·</span>

              <button
                type="button"
                class="link-btn"
                :disabled="loading || iniciandoSistema"
                @click="abrirContacto"
              >
                Contacto
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="login-footer">
        Bamboo Management Suite — v2.0.0 · Coded by PandaCode ©
        {{ new Date().getFullYear() }}
      </div>
    </div>

    <!-- =====================================================
         INICIO DEL SISTEMA
         ===================================================== -->
    <transition name="system-loader">
      <div
        v-if="iniciandoSistema"
        class="system-startup"
      >
        <div class="startup-grid" aria-hidden="true"></div>

        <!-- Detalles ambientales -->
        <div class="startup-corner startup-corner-tl"></div>
        <div class="startup-corner startup-corner-tr"></div>
        <div class="startup-corner startup-corner-bl"></div>
        <div class="startup-corner startup-corner-br"></div>

        <div class="startup-content">
          <!-- Etiqueta -->
          <div class="startup-eyebrow">
            <span class="startup-dot"></span>
            BAMBOO / MANAGEMENT
          </div>

          <!-- Logo -->
          <div class="startup-logo-stage">
            <div class="startup-ring startup-ring-1"></div>
            <div class="startup-ring startup-ring-2"></div>

            <span class="stage-mark stage-mark-top"></span>
            <span class="stage-mark stage-mark-right"></span>
            <span class="stage-mark stage-mark-bottom"></span>
            <span class="stage-mark stage-mark-left"></span>

            <div class="startup-cross startup-cross-top"></div>
            <div class="startup-cross startup-cross-bottom"></div>

            <div class="startup-logo-box">
              <v-img
                :src="logoBambo"
                contain
                width="165"
                class="startup-logo"
              ></v-img>

              <div class="logo-scan"></div>
            </div>
          </div>

          <!-- Estado principal -->
          <div class="startup-status">
            <div class="startup-status-label">
              SYSTEM INITIALIZATION
            </div>

            <transition name="status-change" mode="out-in">
              <div
                :key="estadoInicio"
                class="startup-status-text"
              >
                {{ estadoInicio }}

                <span
                  v-if="mostrarPuntosCarga"
                  class="loading-dots"
                >
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </div>
            </transition>
          </div>

          <!-- Diagnóstico -->
          <div class="startup-diagnostics">
            <div class="diagnostic-row">
              <span>SESSION</span>

              <span class="diagnostic-line"></span>

              <span class="diagnostic-ok">
                READY
              </span>
            </div>

            <div class="diagnostic-row">
              <span>CONFIG</span>

              <span class="diagnostic-line"></span>

              <span
                :class="[
                  'diagnostic-value',
                  configuracionLista ? 'diagnostic-ok' : ''
                ]"
              >
                {{ configuracionLista ? "LOADED" : "LOADING" }}
              </span>
            </div>

            <div class="diagnostic-row">
              <span>ACCESS</span>

              <span class="diagnostic-line"></span>

              <span class="diagnostic-ok">
                {{ accesoRol || "CHECKING" }}
              </span>
            </div>
          </div>

          <!-- Indicador inferior -->
          <div class="startup-footer">
            <span class="startup-version">
              SYSTEM / 02.00.00
            </span>

            <span class="startup-footer-dot"></span>

            <span class="startup-secure">
              SECURE SESSION
            </span>
          </div>
        </div>
      </div>
    </transition>

    <!-- Snackbar -->
    <v-snackbar
      v-model="mostrarMensaje"
      :color="mensaje.color"
      :timeout="3000"
      right
      top
    >
      {{ mensaje.texto }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="mostrarMensaje = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from "../../Servicios/HttpService";
import logoBambo from "@/assets/img/LOGOBAMBO.png";

export default {
  name: "Login",

  data: () => ({
    logoBambo,

    usuario: "",
    password: "",

    mensaje: {
      texto: "",
      color: ""
    },

    mostrarMensaje: false,
    showPassword: false,

    // Estado del login
    loading: false,

    // Estado de inicio del sistema
    iniciandoSistema: false,
    estadoInicio: "Verificando credenciales",
    accesoRol: "",
    configuracionLista: false,
    mostrarPuntosCarga: true
  }),

  created() {
    if (this.$store.getters.isLoggedIn) {
      this.redireccionarPorRol(
        this.$store.getters.userRole
      );
    }
  },

  methods: {
    iniciarSesion() {
      if (!this.usuario || !this.password) {
        this.mostrarMensaje = true;

        this.mensaje.texto = !this.usuario
          ? "Debes colocar el usuario"
          : "Debes colocar la contraseña";

        this.mensaje.color = "warning";

        return;
      }

      this.loading = true;

      const payload = {
        metodo: "login",

        usuario: {
          usuario: this.usuario,
          password: this.password
        }
      };

      HttpService.obtenerConDatos(
        payload,
        "usuarios.php"
      )
        .then(resultado => {
          if (resultado && resultado.resultado) {
            const rol = resultado.datos.rol.toLowerCase();
             const datosUsuario = resultado.datos;

            /*
             * La autenticación ya fue confirmada.
             * Ahora iniciamos la transición visual.
             */
            this.loading = false;

            this.iniciandoSistema = true;
            this.estadoInicio = "Autenticación completada";
            this.accesoRol = resultado.datos.rol;
            this.configuracionLista = false;
            this.mostrarPuntosCarga = false;

            localStorage.setItem(
              "idUsuario",
              resultado.datos.idUsuario
            );

            localStorage.setItem(
              "rol",
              resultado.datos.rol
            );

            localStorage.setItem(
              "usuario",
              resultado.datos.nombreUsuario
            );

            /*
             * Pequeña pausa visual para permitir
             * apreciar el cambio de autenticación.
             */
            setTimeout(() => {
              this.estadoInicio = "Cargando configuración";
              this.mostrarPuntosCarga = true;

              this.cargarAjustesGimnasio(() => {
                this.configuracionLista = true;

                this.estadoInicio =
                  "Preparando espacio de trabajo";

                setTimeout(() => {
                  this.estadoInicio = "Sistema preparado";
                  this.mostrarPuntosCarga = false;

                  setTimeout(() => {
            
                    this.$store.commit("setUser", datosUsuario);

                    this.redireccionarPorRol(rol);
                  }, 450);
                }, 650);
              });
            }, 450);
          } else {
            this.loading = false;

            this.mostrarMensaje = true;

            this.mensaje.texto =
              "Usuario o contraseña incorrectos";

            this.mensaje.color = "error";
          }
        })
        .catch(() => {
          this.loading = false;
          this.iniciandoSistema = false;

          this.mostrarMensaje = true;
          this.mensaje.texto = "Error de conexión";
          this.mensaje.color = "error";
        });
    },

    redireccionarPorRol(rol) {
      switch (rol) {
        case "admin":
        case "administrador":
          this.$router.replace("/dashboard");
          break;

        case "empleado":
          this.$router.replace("/registrar-visita");
          break;

        default:
          this.iniciandoSistema = false;

          this.mostrarMensaje = true;
          this.mensaje.texto =
            "No tienes permisos para acceder.";
          this.mensaje.color = "error";
      }
    },

    abrirSoporte() {
      this.mostrarMensaje = true;

      this.mensaje.texto =
        "Envíanos un mensaje a nava.saidalfredo@gmail.com";

      this.mensaje.color = "info";
    },

    abrirContacto() {
      this.mostrarMensaje = true;

      this.mensaje.texto =
        "Contáctanos mediante Whatsapp al 3531015780";

      this.mensaje.color = "error";
    },

    cargarAjustesGimnasio(callback) {
      HttpService.obtenerConDatos(
        {
          metodo: "obtener"
        },
        "ajustes.php"
      )
        .then(ajustes => {
          if (ajustes) {
            localStorage.setItem(
              "nombreGimnasio",
              ajustes.nombre
            );

            localStorage.setItem(
              "logoGimnasio",
              ajustes.logo
            );
          }
        })
        .finally(() => {
          callback();
        });
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap");

/* =========================================================
   TOKENS
   ========================================================= */

.login-page {
  --bg: #0e0e10;
  --card: #17171a;
  --card-border: #26262a;

  --text-primary: #f2f2ed;
  --text-secondary: #8c8c93;
  --text-tertiary: #5c5c62;

  --accent: #c8ff4d;
  --accent-dim: rgba(200, 255, 77, 0.14);

  --error: #ff6b6b;

  --radius: 4px;

  --font-display: "Space Grotesk", sans-serif;

  --font-body:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;

  --font-mono: "JetBrains Mono", monospace;
}

/* =========================================================
   PAGE
   ========================================================= */

.login-page {
  min-height: 100vh;

  background: var(--bg);
  color: var(--text-primary);

  font-family: var(--font-body);

  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  padding: 32px 20px;
}

/* =========================================================
   BACKGROUND GRID
   ========================================================= */

.bg-grid {
  position: absolute;
  inset: 0;

  background-image:
    linear-gradient(
      rgba(242, 242, 237, 0.028) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(242, 242, 237, 0.028) 1px,
      transparent 1px
    );

  background-size: 48px 48px;

  mask-image: radial-gradient(
    ellipse 70% 60% at 50% 40%,
    black 30%,
    transparent 75%
  );

  pointer-events: none;
}

/* =========================================================
   SHELL
   ========================================================= */

.login-shell {
  position: relative;
  z-index: 2;

  width: 100%;
  max-width: 400px;

  display: flex;
  flex-direction: column;
  align-items: center;
}

/* =========================================================
   EYEBROW
   ========================================================= */

.tag-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  font-family: var(--font-mono);

  font-size: 11px;
  font-weight: 500;

  letter-spacing: 0.14em;

  color: var(--text-secondary);

  margin-bottom: 20px;
}

.tag-dot {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background: var(--accent);

  box-shadow: 0 0 8px var(--accent);
}

/* =========================================================
   CARD
   ========================================================= */

.login-card {
  position: relative;

  width: 100%;

  background: var(--card);

  border: 1px solid var(--card-border);
  border-radius: var(--radius);

  display: flex;

  overflow: hidden;
}

/* =========================================================
   RULER
   ========================================================= */

.ruler {
  width: 18px;

  flex-shrink: 0;

  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: flex-end;

  padding: 24px 0;

  border-right: 1px solid var(--card-border);

  background: rgba(255, 255, 255, 0.012);
}

.tick {
  width: 6px;
  height: 1px;

  background: var(--text-tertiary);

  margin-right: 6px;
}

.tick.major {
  width: 11px;

  background: var(--accent);
}

.login-card-inner {
  flex: 1;

  padding: 40px 32px;

  min-width: 0;
}

/* =========================================================
   HEADER
   ========================================================= */

.login-head {
  margin-bottom: 32px;
}

.login-logo {
  margin-bottom: 20px;

  filter:
    grayscale(1)
    brightness(1.6);

  opacity: 0.92;
}

.login-title {
  font-family: var(--font-display);

  font-size: 24px;
  font-weight: 700;

  letter-spacing: -0.01em;

  margin: 0 0 6px;

  color: var(--text-primary);
}

.login-sub {
  font-size: 13.5px;

  color: var(--text-secondary);

  margin: 0;
}

/* =========================================================
   FORM
   ========================================================= */

.login-form {
  display: flex;
  flex-direction: column;

  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;

  gap: 8px;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.field-label {
  font-size: 12px;
  font-weight: 600;

  letter-spacing: 0.04em;

  color: var(--text-secondary);

  text-transform: uppercase;
}

.field-toggle {
  background: none;
  border: none;

  font-family: var(--font-mono);

  font-size: 11px;

  color: var(--text-tertiary);

  cursor: pointer;

  padding: 0;

  transition: color 0.15s ease;
}

.field-toggle:hover:not(:disabled) {
  color: var(--accent);
}

.field-toggle:disabled {
  cursor: default;

  opacity: 0.4;
}

.field-input {
  width: 100%;

  background: #0e0e10;

  border: 1px solid var(--card-border);
  border-radius: var(--radius);

  padding: 12px 14px;

  font-size: 14.5px;

  font-family: var(--font-body);

  color: var(--text-primary);

  outline: none;

  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    opacity 0.15s ease;
}

.field-input::placeholder {
  color: var(--text-tertiary);
}

.field-input:focus {
  border-color: var(--accent);

  box-shadow:
    0 0 0 3px
    var(--accent-dim);
}

.field-input:disabled {
  opacity: 0.5;
}

/* =========================================================
   SUBMIT
   ========================================================= */

.submit-btn {
  margin-top: 6px;

  width: 100%;

  background: var(--accent);

  color: #0e0e10;

  border: none;

  border-radius: var(--radius);

  padding: 13px;

  font-family: var(--font-body);

  font-size: 14px;
  font-weight: 600;

  letter-spacing: 0.01em;

  cursor: pointer;

  transition:
    filter 0.15s ease,
    transform 0.1s ease,
    opacity 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  filter: brightness(1.08);
}

.submit-btn:active:not(:disabled) {
  transform: scale(0.99);
}

.submit-btn:disabled {
  opacity: 0.7;

  cursor: default;
}

.auth-loading {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 100px;
}

.auth-dots {
  display: inline-flex;

  margin-left: 2px;
}

.auth-dots span {
  opacity: 0;

  animation: loadingDot 1.25s infinite;
}

.auth-dots span:nth-child(2) {
  animation-delay: 0.18s;
}

.auth-dots span:nth-child(3) {
  animation-delay: 0.36s;
}

/* =========================================================
   LINKS
   ========================================================= */

.login-links {
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  margin-top: 8px;

  padding-top: 18px;

  border-top: 1px solid var(--card-border);
}

.link-btn {
  background: none;
  border: none;

  font-size: 12.5px;

  color: var(--text-secondary);

  cursor: pointer;

  padding: 4px;

  transition:
    color 0.15s ease,
    opacity 0.15s ease;
}

.link-btn:hover:not(:disabled) {
  color: var(--text-primary);
}

.link-btn:disabled {
  opacity: 0.35;

  cursor: default;
}

.link-sep {
  color: var(--text-tertiary);

  font-size: 12px;
}

/* =========================================================
   FOOTER
   ========================================================= */

.login-footer {
  margin-top: 28px;

  font-family: var(--font-mono);

  font-size: 10.5px;

  letter-spacing: 0.03em;

  color: var(--text-tertiary);

  text-align: center;
}

/* =========================================================
   LOGIN MOTION
   ========================================================= */

.fade-in {
  animation: fadeIn 0.5s ease both;
}

.fade-in-up {
  animation:
    fadeInUp
    0.5s ease
    0.05s both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;

    transform:
      translateY(10px);
  }

  to {
    opacity: 1;

    transform:
      translateY(0);
  }
}

/* =========================================================
   SYSTEM STARTUP
   ========================================================= */

.system-startup {
  position: fixed;
  inset: 0;

  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #0e0e10;

  overflow: hidden;
}

/* =========================================================
   STARTUP GRID
   ========================================================= */

.startup-grid {
  position: absolute;
  inset: 0;

  background-image:
    linear-gradient(
      rgba(242, 242, 237, 0.026) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(242, 242, 237, 0.026) 1px,
      transparent 1px
    );

  background-size: 48px 48px;

  mask-image: radial-gradient(
    ellipse 65% 62% at 50% 50%,
    black 20%,
    transparent 78%
  );

  pointer-events: none;
}

/* =========================================================
   SCREEN CORNERS
   ========================================================= */

.startup-corner {
  position: absolute;

  width: 24px;
  height: 24px;

  opacity: 0.28;
}

.startup-corner::before,
.startup-corner::after {
  content: "";

  position: absolute;

  background: var(--accent);
}

.startup-corner::before {
  width: 24px;
  height: 1px;
}

.startup-corner::after {
  width: 1px;
  height: 24px;
}

.startup-corner-tl {
  top: 28px;
  left: 28px;
}

.startup-corner-tr {
  top: 28px;
  right: 28px;

  transform: rotate(90deg);
}

.startup-corner-bl {
  left: 28px;
  bottom: 28px;

  transform: rotate(-90deg);
}

.startup-corner-br {
  right: 28px;
  bottom: 28px;

  transform: rotate(180deg);
}

/* =========================================================
   STARTUP CONTENT
   ========================================================= */

.startup-content {
  position: relative;

  z-index: 2;

  width: min(
    420px,
    calc(100vw - 40px)
  );

  display: flex;
  flex-direction: column;
  align-items: center;

  font-family: var(--font-body);

  animation:
    startupContentIn
    0.45s ease both;
}

@keyframes startupContentIn {
  from {
    opacity: 0;

    transform:
      translateY(8px)
      scale(0.99);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }
}

/* =========================================================
   STARTUP EYEBROW
   ========================================================= */

.startup-eyebrow {
  display: flex;
  align-items: center;

  gap: 8px;

  margin-bottom: 36px;

  font-family: var(--font-mono);

  font-size: 10px;

  letter-spacing: 0.17em;

  color: var(--text-secondary);
}

.startup-dot {
  width: 5px;
  height: 5px;

  border-radius: 50%;

  background: var(--accent);

  box-shadow:
    0 0 8px var(--accent),
    0 0 18px rgba(200, 255, 77, 0.4);

  animation:
    startupDot
    1.4s ease-in-out infinite;
}

@keyframes startupDot {
  0%,
  100% {
    opacity: 0.4;

    transform:
      scale(0.9);
  }

  50% {
    opacity: 1;

    transform:
      scale(1.1);
  }
}

/* =========================================================
   LOGO STAGE
   ========================================================= */

.startup-logo-stage {
  position: relative;

  width: 270px;
  height: 210px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 28px;
}

/* =========================================================
   RINGS
   ========================================================= */

.startup-ring {
  position: absolute;

  border:
    1px solid
    rgba(200, 255, 77, 0.14);

  border-radius: 50%;

  pointer-events: none;
}

.startup-ring-1 {
  width: 210px;
  height: 210px;

  border-left-color:
    rgba(200, 255, 77, 0.7);

  border-right-color:
    rgba(200, 255, 77, 0.03);

  animation:
    ringRotate
    8s linear infinite;
}

.startup-ring-2 {
  width: 175px;
  height: 175px;

  border-top-color:
    rgba(200, 255, 77, 0.5);

  border-bottom-color:
    transparent;

  animation:
    ringRotateReverse
    5s linear infinite;
}

@keyframes ringRotate {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ringRotateReverse {
  to {
    transform: rotate(-360deg);
  }
}

/* =========================================================
   RING MARKERS
   ========================================================= */

.stage-mark {
  position: absolute;

  background: var(--accent);

  opacity: 0.6;
}

.stage-mark-top,
.stage-mark-bottom {
  width: 14px;
  height: 1px;
}

.stage-mark-left,
.stage-mark-right {
  width: 1px;
  height: 14px;
}

.stage-mark-top {
  top: 0;
  left: 50%;

  transform:
    translateX(-50%);
}

.stage-mark-bottom {
  bottom: 0;
  left: 50%;

  transform:
    translateX(-50%);
}

.stage-mark-left {
  left: 30px;
  top: 50%;

  transform:
    translateY(-50%);
}

.stage-mark-right {
  right: 30px;
  top: 50%;

  transform:
    translateY(-50%);
}

/* =========================================================
   LOGO
   ========================================================= */

.startup-logo-box {
  position: relative;

  width: 160px;
  height: 105px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  animation:
    logoPulse
    2.5s ease-in-out infinite;
}

.startup-logo {
  filter:
    grayscale(1)
    brightness(1.8);

  opacity: 0.96;
}

@keyframes logoPulse {
  0%,
  100% {
    opacity: 0.76;

    transform:
      scale(0.985);
  }

  50% {
    opacity: 1;

    transform:
      scale(1);
  }
}

/* =========================================================
   LOGO SCANNER
   ========================================================= */

.logo-scan {
  position: absolute;

  left: 0;
  right: 0;

  height: 1px;

  background: var(--accent);

  box-shadow:
    0 0 7px var(--accent),
    0 0 14px rgba(200, 255, 77, 0.5);

  opacity: 0;

  animation:
    logoScan
    2.1s ease-in-out infinite;
}

.logo-scan::after {
  content: "";

  position: absolute;

  left: 0;
  right: 0;
  top: 0;

  height: 28px;

  transform:
    translateY(-28px);

  background:
    linear-gradient(
      to top,
      rgba(200, 255, 77, 0.07),
      transparent
    );
}

@keyframes logoScan {
  0% {
    top: 0;

    opacity: 0;
  }

  12% {
    opacity: 0.9;
  }

  75% {
    opacity: 0.7;
  }

  100% {
    top: 100%;

    opacity: 0;
  }
}

/* =========================================================
   CROSS MARKS
   ========================================================= */

.startup-cross {
  position: absolute;

  width: 6px;
  height: 6px;

  opacity: 0.55;
}

.startup-cross::before,
.startup-cross::after {
  content: "";

  position: absolute;

  background: var(--accent);
}

.startup-cross::before {
  width: 6px;
  height: 1px;

  left: 0;
  top: 2px;
}

.startup-cross::after {
  width: 1px;
  height: 6px;

  top: 0;
  left: 2px;
}

.startup-cross-top {
  top: 13px;
  right: 25px;
}

.startup-cross-bottom {
  bottom: 18px;
  left: 30px;
}

/* =========================================================
   STATUS
   ========================================================= */

.startup-status {
  min-height: 58px;

  text-align: center;
}

.startup-status-label {
  margin-bottom: 7px;

  font-family: var(--font-mono);

  font-size: 9px;

  letter-spacing: 0.19em;

  color: var(--text-tertiary);
}

.startup-status-text {
  font-family: var(--font-display);

  font-size: 15px;
  font-weight: 500;

  color: var(--text-primary);
}

/* =========================================================
   LOADING DOTS
   ========================================================= */

.loading-dots span {
  opacity: 0;

  animation:
    loadingDot
    1.3s infinite;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.18s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.36s;
}

@keyframes loadingDot {
  0%,
  20% {
    opacity: 0;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

/* =========================================================
   STATUS TRANSITION
   ========================================================= */

.status-change-enter-active,
.status-change-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.status-change-enter {
  opacity: 0;

  transform:
    translateY(4px);
}

.status-change-leave-to {
  opacity: 0;

  transform:
    translateY(-4px);
}

/* =========================================================
   DIAGNOSTICS
   ========================================================= */

.startup-diagnostics {
  width: 270px;

  margin-top: 26px;
  padding-top: 15px;

  border-top:
    1px solid
    var(--card-border);

  display: flex;
  flex-direction: column;

  gap: 7px;
}

.diagnostic-row {
  display: flex;
  align-items: center;

  font-family: var(--font-mono);

  font-size: 8px;

  letter-spacing: 0.1em;

  color: var(--text-tertiary);
}

.diagnostic-line {
  flex: 1;

  height: 1px;

  margin: 0 10px;

  background-image:
    linear-gradient(
      90deg,
      var(--card-border) 40%,
      transparent 40%
    );

  background-size: 5px 1px;
}

.diagnostic-value {
  color: var(--text-secondary);

  text-transform: uppercase;
}

.diagnostic-ok {
  color: var(--accent);

  text-transform: uppercase;
}

/* =========================================================
   STARTUP FOOTER
   ========================================================= */

.startup-footer {
  display: flex;
  align-items: center;

  gap: 9px;

  margin-top: 34px;

  font-family: var(--font-mono);

  font-size: 8px;

  letter-spacing: 0.14em;
}

.startup-version {
  color: #414146;
}

.startup-footer-dot {
  width: 3px;
  height: 3px;

  border-radius: 50%;

  background: #414146;
}

.startup-secure {
  color: #414146;
}

/* =========================================================
   OVERLAY TRANSITION
   ========================================================= */

.system-loader-enter-active {
  transition:
    opacity 0.28s ease;
}

.system-loader-leave-active {
  transition:
    opacity 0.28s ease;
}

.system-loader-enter,
.system-loader-leave-to {
  opacity: 0;
}

/* =========================================================
   REDUCED MOTION
   ========================================================= */

@media (prefers-reduced-motion: reduce) {
  .fade-in,
  .fade-in-up,
  .startup-content,
  .startup-dot,
  .startup-ring,
  .startup-logo-box,
  .logo-scan,
  .loading-dots span,
  .auth-dots span {
    animation: none;
  }
}

/* =========================================================
   RESPONSIVE
   ========================================================= */

@media (max-width: 480px) {
  .login-page {
    padding: 24px 16px;
  }

  .login-card-inner {
    padding: 28px 20px;
  }

  .ruler {
    display: none;
  }

  .startup-logo-stage {
    transform: scale(0.9);

    margin-top: -10px;
    margin-bottom: 18px;
  }

  .startup-diagnostics {
    width: 250px;
  }

  .startup-corner {
    display: none;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Usuarios/Login.vue